import { Filter, FilterAndSortProps } from '@legacy-vfuk/core-filter-and-sort/dist/FilterAndSort.types';
import FilterFlyout from '@legacy-vfuk/core-filter-flyout';
import Modal from '@legacy-vfuk/core-modal';
import React, { FC, useEffect, useState } from 'react';

import { SWFilterFlyoutProps } from './SWFilterFlyout.types';
import Button from '@legacy-vfuk/core-button';
import { navigate } from 'gatsby';

interface OptionProps {
  filter: Filter;
  item: Filter['items'][0];
  onChange: (id: string, updated: Filter) => void;
}

const CheckboxOption: FC<OptionProps> = ({ filter, item, onChange }) => {
  const toggle = () => {
    const updatedItems = filter.items.map(i => (i.value === item.value ? { ...i, checked: !i.checked } : i));
    onChange(filter.id, { ...filter, items: updatedItems });
  };

  return (
    <label style={{ display: 'block', margin: '0.25rem' }}>
      <input style={{ margin: '1rem' }} type="checkbox" checked={item.checked} onChange={toggle} />
      {item.label}
      <hr />
    </label>
  );
};

const RadioOption: FC<OptionProps> = ({ filter, item, onChange }) => {
  const select = () => {
    onChange(filter.id, { ...filter, checked: item.value });
  };

  return (
    <label style={{ display: 'block', margin: '0.25rem 0' }}>
      <input
        type="radio"
        name={filter.options?.groupName || filter.id}
        checked={filter.checked === item.value}
        onChange={select}
      />
      {item.label}
    </label>
  );
};

const SWFilterFlyout: FC<SWFilterFlyoutProps> = ({ ...props }: SWFilterFlyoutProps) => {
  const filtersData = props.filters.map(
    ({ filterId, filterTitle, filterType, filterGroupName, subText, checked, items }) => ({
      ...(subText && { subText }),
      id: filterId,
      title: filterTitle,
      type: filterType,
      ...(['radio', 'checkbox'].includes(filterType) && { options: { groupName: filterGroupName } }),
      ...('radio' === filterType && { checked }),
      items,
    })
  ) as FilterAndSortProps['filters'];

  const [filters, setFilters] = useState<FilterAndSortProps['filters']>(filtersData);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // check if we have filters applied , if not model will be the same
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const raw = urlParams.get('filter');
    if (!raw) return;

    const selected = raw.split('&');

    // Top‐level mapper
    const newFilters = filters.map(filter => applyFilterUpdate(filter, selected));
    setFilters(newFilters);
  }, []);

  /**
   * Dispatch to the correct updater based on filter.type.
   */
  function applyFilterUpdate(filter: Filter, selected: string[]): Filter {
    if (filter.type === 'checkbox') {
      return updateCheckboxFilter(filter, selected);
    }
    if (filter.type === 'radio') {
      return updateRadioFilter(filter, selected);
    }
    return filter;
  }

  /**
   * For checkbox filters, toggle each item's `.checked`.
   */
  function updateCheckboxFilter(filter: Filter, selected: string[]): Filter {
    const items = filter.items.map(item => ({
      ...item,
      checked: selected.includes(item.label),
    }));
    return { ...filter, items };
  }

  /**
   * For radio filters, pick the single checked value.
   */
  function updateRadioFilter(filter: Filter, selected: string[]): Filter {
    const checked = selected.find(val => filter.items.some(item => item.label === val));
    return { ...filter, checked };
  }

  const handleOnChange = (id: string, updatedFilter: Filter) => {
    const params = new URLSearchParams(window.location.search);
    const updatedFilters = filters.map(filter => {
      if (id === filter.id) return { ...updatedFilter };
      return filter;
    });
    setFilters(updatedFilters);

    const filtersString = updatedFilters[0].items
      .filter(item => item.checked)
      .map(item => item.label)
      .join('&'); // Convert the array of filters into a query string format

    params.set('filter', filtersString);
    navigate(`?${params.toString()}`);
  };

  const handleOnClear = () => {
    const updatedFilters = filters.map(filter => ({
      ...filter,
      ...('toggle' === filter.type && { isActive: false }),
      ...('radio' === filter.type && { checked: undefined }),
      ...('checkbox' === filter.type && {
        items: filter.items.map(item => ({ ...item, checked: undefined })),
      }),
    })) as Filter[];

    setFilters(updatedFilters);
  };

  return (
    <>
      <Button
        icon={{ name: 'filter', justify: 'left' }}
        text="Filter"
        appearance="alt1"
        onClick={() => setIsOpen(true)}
      />
      <Modal isOpen={isOpen} onCloseCb={() => setIsOpen(false)} type="flyout" side="right" srName="Filters">
        <div style={{ padding: '1rem' }}>
          {filters.map(filter => (
            <div key={filter.id} style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '2.2rem', fontWeight: 500, margin: '0 0 0.5rem' }}>{filter.title}</h4>

              {filter.type === 'checkbox' &&
                filter.items.map(item => (
                  <CheckboxOption key={item.value} filter={filter} item={item} onChange={handleOnChange} />
                ))}

              {filter.type === 'radio' &&
                filter.items.map(item => (
                  <RadioOption key={item.value} filter={filter} item={item} onChange={handleOnChange} />
                ))}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default SWFilterFlyout;
